/**
 * Automatic Shiprocket Synchronization
 * This utility handles automatic creation, tracking, and synchronization of orders with Shiprocket
 */

// import firebase from 'firebase/app';
import 'firebase/firestore';
import { db } from '../firebase/config';
import { BACKEND_URL } from './env';

// Configuration
const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const RETRY_DELAY = 30 * 1000; // 30 seconds

/**
 * Automatically create Shiprocket order when a new order is placed
 */
export const autoCreateShiprocketOrder = async (orderData) => {
  try {
    // Check if order already has Shiprocket data to prevent duplicates
    if (orderData.shiprocketOrderId && orderData.shiprocketCreated) {
      return {
        success: true,
        shiprocketOrderId: orderData.shiprocketOrderId,
        message: 'Order already exists in Shiprocket'
      };
    }
    
    // Prepare order data for Shiprocket API
    const shiprocketOrderData = {
      orderId: orderData.orderId,
      orderDate: orderData.createdAt?.toDate ? orderData.createdAt.toDate().toISOString() : new Date(orderData.createdAt || orderData.timestamp).toISOString(),
      billingCustomerName: orderData.customerInfo?.name || 'Customer',
      billingLastName: "",
      billingAddress: orderData.customerInfo?.address?.street || '',
      billingAddress2: "",
      billingCity: orderData.customerInfo?.address?.city || '',
      billingPincode: orderData.customerInfo?.address?.pincode || '',
      billingState: orderData.customerInfo?.address?.state || '',
      billingCountry: orderData.customerInfo?.address?.country || 'India',
      billingPhone: orderData.customerInfo?.phone || '',
      billingEmail: orderData.customerInfo?.email || '',
      shippingIsBilling: true,
      orderItems: (orderData.cartItems || []).map(item => ({
        name: item.name || 'Product',
        sku: item.id || 'SKU001',
        units: item.quantity || 1,
        sellingPrice: item.price || 0
      })),
      paymentMethod: orderData.paymentInfo?.method === 'cod' ? 'COD' : 'Prepaid',
      subTotal: orderData.paymentInfo?.amount || orderData.total || 0,
      length: 30, // Default dimensions in cm
      breadth: 20,
      height: 5,
      weight: 0.5 // Default weight in kg
    };
    
    // Create order in Shiprocket via backend API
    const backendUrl = BACKEND_URL;
    const response = await fetch(`${backendUrl}/api/shiprocket/orders/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(shiprocketOrderData)
    });
    
    const result = await response.json();
    
    if (result.success && result.data) {
      // Update Firestore order with Shiprocket data
      const orderRef = db.collection('orders').doc(orderData.id);
      
      const updateData = {
        shiprocketOrderId: result.data.order_id || result.data.channel_order_id,
        shiprocketStatus: 'NEW',
        shiprocketCreated: true,
        shiprocketLastUpdated: new Date(),
        updatedAt: new Date()
      };
      
      await orderRef.update(updateData);
      
      // Start automatic AWB monitoring for this order
      setTimeout(() => {
        autoFetchAWBCode(result.data.order_id, orderData.id);
      }, 60000); // Check for AWB after 1 minute
      
      return {
        success: true,
        shiprocketOrderId: result.data.order_id,
        message: 'Order automatically created in Shiprocket'
      };
    } else {
      throw new Error(result.message || 'Failed to create order in Shiprocket');
    }
    
  } catch (error) {
    console.error('Error auto-creating Shiprocket order:', error);
    
    // Mark order for retry
    const orderRef = db.collection('orders').doc(orderData.id);
    await orderRef.update({
      shiprocketRetryCount: (orderData.shiprocketRetryCount || 0) + 1,
      shiprocketLastError: error.message,
      shiprocketLastRetry: new Date(),
      updatedAt: new Date()
    });
    
    return {
      success: false,
      error: error.message,
      message: 'Failed to auto-create order in Shiprocket'
    };
  }
};

/**
 * Automatically fetch AWB code for a Shiprocket order
 */
export const autoFetchAWBCode = async (shiprocketOrderId, firestoreOrderId, retryCount = 0) => {
  try {
    const backendUrl = BACKEND_URL;
    const response = await fetch(`${backendUrl}/api/shiprocket/orders/${shiprocketOrderId}/awb`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const result = await response.json();
    
    if (result.success && result.data && (result.data.awbCode || result.data.awb_code)) {
      // Update Firestore order with AWB data
      const orderRef = db.collection('orders').doc(firestoreOrderId);
      
      const awbCode = result.data.awbCode || result.data.awb_code;
      const updateData = {
        shiprocketAWB: awbCode,
        awbCode: awbCode,
        carrier: result.data.courierName || result.data.courier_name || 'India Post',
        trackingUrl: `https://www.shiprocket.in/tracking/${awbCode}`,
        shiprocketStatus: result.data.status || 'SHIPPED',
        shiprocketLastUpdated: new Date(),
        updatedAt: new Date()
      };
      
      await orderRef.update(updateData);
      
      // Start automatic tracking monitoring
      setTimeout(() => {
        autoSyncTrackingData(awbCode, firestoreOrderId);
      }, 30000); // Start tracking sync after 30 seconds
      
      return {
        success: true,
        awbCode: awbCode,
        message: 'AWB code automatically fetched and stored'
      };
    } else if (retryCount < MAX_RETRIES) {
      // Retry if no AWB yet
      setTimeout(() => {
        autoFetchAWBCode(shiprocketOrderId, firestoreOrderId, retryCount + 1);
      }, RETRY_DELAY);
      
      return {
        success: false,
        message: 'AWB not ready yet, will retry',
        retryCount: retryCount + 1
      };
    } else {
      return {
        success: false,
        message: 'Max retries reached for AWB fetch'
      };
    }
    
  } catch (error) {
    console.error('Error auto-fetching AWB:', error);
    
    if (retryCount < MAX_RETRIES) {
      setTimeout(() => {
        autoFetchAWBCode(shiprocketOrderId, firestoreOrderId, retryCount + 1);
      }, RETRY_DELAY);
    }
    
    return {
      success: false,
      error: error.message,
      message: 'Failed to auto-fetch AWB'
    };
  }
};

/**
 * Automatically sync tracking data for an AWB code
 */
export const autoSyncTrackingData = async (awbCode, firestoreOrderId, retryCount = 0) => {
  try {
    const backendUrl = BACKEND_URL;
    const response = await fetch(`${backendUrl}/api/shiprocket/track/${awbCode}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const result = await response.json();
    
    if (result.success && result.trackingData) {
      // Update Firestore order with tracking data
      const orderRef = db.collection('orders').doc(firestoreOrderId);
      
      const updateData = {
        trackingData: result.trackingData,
        shiprocketStatus: result.trackingData.status || 'IN_TRANSIT',
        trackingLastUpdated: new Date(),
        updatedAt: new Date()
      };
      
      await orderRef.update(updateData);
      
      // Schedule next tracking sync
      setTimeout(() => {
        autoSyncTrackingData(awbCode, firestoreOrderId);
      }, SYNC_INTERVAL);
      
      return {
        success: true,
        trackingData: result.trackingData,
        message: 'Tracking data automatically synced'
      };
    } else {
      // Retry tracking sync
      setTimeout(() => {
        autoSyncTrackingData(awbCode, firestoreOrderId, retryCount + 1);
      }, SYNC_INTERVAL);
      
      return {
        success: false,
        message: 'Tracking data not available yet, will retry'
      };
    }
    
  } catch (error) {
    console.error('Error auto-syncing tracking data:', error);
    
    // Retry on error
    setTimeout(() => {
      autoSyncTrackingData(awbCode, firestoreOrderId, retryCount + 1);
    }, SYNC_INTERVAL);
    
    return {
      success: false,
      error: error.message,
      message: 'Failed to auto-sync tracking data'
    };
  }
};

/**
 * Process all pending orders for automatic Shiprocket sync
 */
export const processPendingOrders = async () => {
  try {
    let pendingOrders = [];
    
    try {
      // Try simple query first
      const ordersSnapshot = await db.collection('orders')
        .where('shiprocketCreated', '==', false)
        .limit(10)
        .get();
      
      const allOrders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter orders that need processing (client-side filtering to avoid composite index)
      pendingOrders = allOrders.filter(order => {
        const retryCount = order.shiprocketRetryCount || 0;
        return retryCount < MAX_RETRIES;
      });
    } catch (queryError) {
      
      // Fallback: Get all orders and filter client-side
      const allOrdersSnapshot = await db.collection('orders')
        .orderBy('createdAt', 'desc')
        .limit(50)
        .get();
      
      const allOrders = allOrdersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter orders that need Shiprocket processing
      pendingOrders = allOrders.filter(order => {
        const shiprocketCreated = order.shiprocketCreated || false;
        const retryCount = order.shiprocketRetryCount || 0;
        return !shiprocketCreated && retryCount < MAX_RETRIES;
      });
    }
    
    for (const order of pendingOrders) {
      try {
        await autoCreateShiprocketOrder(order);
        // Add delay between orders to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        console.error(`Error processing order ${order.orderId}:`, error);
      }
    }
    
    return {
      success: true,
      processed: pendingOrders.length,
      message: `Processed ${pendingOrders.length} pending orders`
    };
    
  } catch (error) {
    console.error('Error processing pending orders:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to process pending orders'
    };
  }
};

/**
 * Process all orders with Shiprocket IDs but no AWB codes
 */
export const processOrdersForAWB = async () => {
  try {
    let ordersNeedingAWB = [];
    
    try {
      // Try simple query first
      const ordersSnapshot = await db.collection('orders')
        .where('shiprocketOrderId', '!=', null)
        .limit(20)
        .get();
      
      const allOrdersWithShiprocket = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter orders that need AWB codes (client-side filtering to avoid composite index)
      ordersNeedingAWB = allOrdersWithShiprocket.filter(order => {
        return !order.shiprocketAWB && !order.awbCode;
      });
    } catch (queryError) {
      
      // Fallback: Get all orders and filter client-side
      const allOrdersSnapshot = await db.collection('orders')
        .orderBy('createdAt', 'desc')
        .limit(50)
        .get();
      
      const allOrders = allOrdersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter orders that need AWB codes
      ordersNeedingAWB = allOrders.filter(order => {
        const hasShiprocketId = order.shiprocketOrderId && order.shiprocketOrderId !== null;
        const hasNoAWB = !order.shiprocketAWB && !order.awbCode;
        return hasShiprocketId && hasNoAWB;
      });
    }
    
    for (const order of ordersNeedingAWB) {
      try {
        await autoFetchAWBCode(order.shiprocketOrderId, order.id);
        // Add delay between requests
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`Error fetching AWB for order ${order.orderId}:`, error);
      }
    }
    
    return {
      success: true,
      processed: ordersNeedingAWB.length,
      message: `Processed ${ordersNeedingAWB.length} orders for AWB fetch`
    };
    
  } catch (error) {
    console.error('Error processing orders for AWB:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to process orders for AWB'
    };
  }
};

/**
 * Start automatic synchronization service
 */
export const startAutomaticSync = () => {
  // Process pending orders every 2 minutes
  setInterval(() => {
    processPendingOrders();
  }, 2 * 60 * 1000);
  
  // Process AWB fetching every 3 minutes
  setInterval(() => {
    processOrdersForAWB();
  }, 3 * 60 * 1000);
  
  // Initial processing
  setTimeout(() => {
    processPendingOrders();
    processOrdersForAWB();
  }, 10000); // Start after 10 seconds
};

/**
 * Stop automatic synchronization service
 */
export const stopAutomaticSync = () => {
  // Note: In a real implementation, you'd store the interval IDs and clear them
};